import type { SortDescriptor } from "react-aria-components";

import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

import {
	type ListRestoreSnapshot,
	markListRestorePending,
} from "../utils/list-restore-store";
import {
	findScrollableParent,
	getAnchorViewportOffset,
	type MarkAnchorOptions,
} from "../utils/scroll-container";
import { useListRestore } from "./useListRestore";

/**
 * Wires table UI state (page, sort, search, row anchor) to the list-restore
 * store. Persists on unmount and when the user paginates/sorts; `markAnchor`
 * runs on row click or keyboard activation before detail navigation.
 */
type RestoredTableState = {
	currentPage: number;
	markAnchor: (
		id: string,
		element?: HTMLElement | null,
		options?: MarkAnchorOptions,
	) => void;
	/** Pass to `useRestoreTableAnchorScroll` as `onAnchorConsumed`. */
	onAnchorConsumed: () => void;
	pageSize: number;
	restoreAnchorId: string | undefined;
	restoreAnchorOffset: number | undefined;
	restoreAnchorViaKeyboard: boolean | undefined;
	searchQuery: string;
	setCurrentPage: (page: number) => void;
	setPageSize: (size: number) => void;
	setSearchQuery: (query: string) => void;
	setSortDescriptor: (sort: SortDescriptor) => void;
	shouldRestore: boolean;
	sortDescriptor: SortDescriptor;
};

const seedTableStateFromSnapshot = (options: {
	defaultPageSize: number;
	defaultSort: SortDescriptor;
	persistQueryState: boolean;
	snapshot: ListRestoreSnapshot | undefined;
}) => {
	const { defaultPageSize, defaultSort, persistQueryState, snapshot } = options;
	return {
		anchorId: snapshot?.anchorId,
		anchorOffset: snapshot?.anchorOffset,
		anchorViaKeyboard: snapshot?.anchorViaKeyboard,
		currentPage: snapshot?.page ?? 1,
		pageSize: persistQueryState
			? (snapshot?.pageSize ?? defaultPageSize)
			: defaultPageSize,
		searchQuery: persistQueryState ? (snapshot?.searchQuery ?? "") : "",
		sortDescriptor:
			persistQueryState && snapshot?.sortColumn
				? {
						column: snapshot.sortColumn,
						direction: snapshot.sortDirection ?? defaultSort.direction,
					}
				: defaultSort,
	};
};

/**
 * Table page/sort/search/pageSize initialized from a restore snapshot.
 * Latest values are written to the in-memory store on unmount, on page/sort/
 * search change, and when a row is clicked (`markAnchor`).
 */
export const useRestoredTableState = (options: {
	defaultPageSize?: number;
	defaultSort: SortDescriptor;
	listKey: string;
	/** When false, only page + row anchor are restored (sort/search live elsewhere). */
	persistQueryState?: boolean;
}): RestoredTableState => {
	const {
		defaultPageSize = 10,
		defaultSort,
		listKey,
		persistQueryState = true,
	} = options;
	const { save, shouldRestore, snapshot } = useListRestore(listKey);

	const seededKeyRef = useRef(listKey);
	const initialSeed = seedTableStateFromSnapshot({
		defaultPageSize,
		defaultSort,
		persistQueryState,
		snapshot,
	});

	const [currentPage, setCurrentPage] = useState(() => initialSeed.currentPage);
	const [pageSize, setPageSize] = useState(() => initialSeed.pageSize);
	const [searchQuery, setSearchQuery] = useState(() => initialSeed.searchQuery);
	const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>(
		() => initialSeed.sortDescriptor,
	);

	const anchorRef = useRef({
		anchorId: initialSeed.anchorId,
		anchorOffset: initialSeed.anchorOffset,
		anchorViaKeyboard: initialSeed.anchorViaKeyboard,
	});
	const persistRef = useRef<() => void>(() => {});

	const skipNextPersistRef = useRef(true);

	/* eslint-disable react-hooks/exhaustive-deps -- defaultSort is often an inline object; compare column/direction only */
	useLayoutEffect(() => {
		if (seededKeyRef.current === listKey) {
			return;
		}
		seededKeyRef.current = listKey;

		const nextSeed = seedTableStateFromSnapshot({
			defaultPageSize,
			defaultSort,
			persistQueryState,
			snapshot,
		});
		setCurrentPage(nextSeed.currentPage);
		setPageSize(nextSeed.pageSize);
		setSearchQuery(nextSeed.searchQuery);
		setSortDescriptor(nextSeed.sortDescriptor);
		anchorRef.current = {
			anchorId: nextSeed.anchorId,
			anchorOffset: nextSeed.anchorOffset,
			anchorViaKeyboard: nextSeed.anchorViaKeyboard,
		};
		skipNextPersistRef.current = true;
	}, [
		defaultPageSize,
		defaultSort.column,
		defaultSort.direction,
		listKey,
		persistQueryState,
		snapshot,
	]);
	/* eslint-enable react-hooks/exhaustive-deps */

	const clearAnchor = useCallback(() => {
		anchorRef.current.anchorId = undefined;
		anchorRef.current.anchorOffset = undefined;
		anchorRef.current.anchorViaKeyboard = false;
	}, []);

	const prevPageRef = useRef(currentPage);

	// Paginating clears the row anchor so a stale id from another page does not
	// trigger AC8 (reset to page 1) after keyboard navigation without markAnchor.
	useEffect(() => {
		if (prevPageRef.current === currentPage) {
			return;
		}
		prevPageRef.current = currentPage;
		clearAnchor();
	}, [clearAnchor, currentPage]);

	useEffect(() => {
		persistRef.current = () => {
			const next: ListRestoreSnapshot = {
				page: currentPage,
			};
			if (persistQueryState) {
				next.pageSize = pageSize;
				next.searchQuery = searchQuery;
				next.sortColumn = sortDescriptor.column;
				next.sortDirection = sortDescriptor.direction;
			}
			if (anchorRef.current.anchorId) {
				next.anchorId = anchorRef.current.anchorId;
			}
			if (anchorRef.current.anchorOffset !== undefined) {
				next.anchorOffset = anchorRef.current.anchorOffset;
			}
			if (anchorRef.current.anchorViaKeyboard) {
				next.anchorViaKeyboard = true;
			}
			save(next);
		};
		if (skipNextPersistRef.current) {
			skipNextPersistRef.current = false;
			return;
		}
		persistRef.current();
	}, [
		currentPage,
		pageSize,
		persistQueryState,
		save,
		searchQuery,
		sortDescriptor,
	]);

	const persist = useCallback(() => {
		persistRef.current();
	}, []);

	const markAnchor = useCallback(
		(id: string, element?: HTMLElement | null, options?: MarkAnchorOptions) => {
			// Real-world case: only call this when the user is actually leaving
			// for this row’s detail (title/View link, etc.). Checkbox, Export, and
			// Manage-modal taps must not set the pending flag, or Back restores
			// as if they had opened that row.
			anchorRef.current.anchorId = id;
			anchorRef.current.anchorViaKeyboard = options?.viaKeyboard ?? false;
			const container = element ? findScrollableParent(element) : null;
			if (element && container) {
				anchorRef.current.anchorOffset = getAnchorViewportOffset(
					element,
					container,
				);
			}
			markListRestorePending(listKey);
			persist();
		},
		[listKey, persist],
	);

	useEffect(() => {
		return () => {
			persist();
		};
	}, [persist]);

	return {
		currentPage,
		markAnchor,
		// The saved row has served its purpose once we have scrolled to it (or
		// found it gone). Dropping it here keeps the unmount persist from
		// re-saving that row and its old screen position, which would send the
		// user back to a spot they had already scrolled away from.
		onAnchorConsumed: clearAnchor,
		pageSize,
		restoreAnchorId: snapshot?.anchorId,
		restoreAnchorOffset: snapshot?.anchorOffset,
		restoreAnchorViaKeyboard: snapshot?.anchorViaKeyboard,
		searchQuery,
		setCurrentPage,
		setPageSize,
		setSearchQuery,
		setSortDescriptor,
		shouldRestore,
		sortDescriptor,
	};
};
